import { CreateTransient } from "@mindlercare/ui-web";
import { defaultTheme } from "@mindlercare/ui-web/src/shared/constants";
import { ColumnDef } from "@tanstack/react-table";
import StatusIndicator from "components/lib/statusIndicator";
import { useDemo } from "contexts/DemoContext";
import { QuestionnaireTableRow } from "core/types/index";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import { Typography } from "components/typography";

export const columns: ColumnDef<QuestionnaireTableRow>[] = [
  {
    accessorKey: "title",
    header: "PATIENT_QUESTIONNAIRE_TABLE_HEADER_TITLE",
    cell: ({ getValue }) => {
      return (
        <QuestionnaireTitle
          value={
            getValue() as {
              title: QuestionnaireTableRow["title"];
              isNew: boolean;
            }
          }
        />
      );
    },
    accessorFn: (row) => ({
      title: row.title,
      isNew: row.isNew,
    }),
  },
  {
    accessorKey: "dateSent",
    header: "PATIENT_QUESTIONNAIRE_TABLE_HEADER_DATE_SENT",
  },
  {
    accessorKey: "sentBy",
    header: "PATIENT_QUESTIONNAIRE_TABLE_HEADER_SENT_BY",
    meta: { sensitive: true },
  },
  {
    accessorKey: "score",
    header: "PATIENT_QUESTIONNAIRE_TABLE_HEADER_SCORE",
    cell: ({ getValue }) => {
      return (
        <QuestionnaireScore
          score={getValue() as QuestionnaireTableRow["score"]}
        />
      );
    },
  },
  {
    accessorKey: "dateCompleted",
    header: "PATIENT_QUESTIONNAIRE_TABLE_HEADER_DATE_COMPLETED",
  },
];

const QuestionnaireTitle = ({
  value,
}: {
  value: {
    title: QuestionnaireTableRow["title"];
    isNew: boolean;
  };
}) => {
  const { t } = useTranslation();
  if (!value.title.value) return "--";

  return (
    <Typography variant="body">
      <FlexHorizontal $gap="XS">
        {value.isNew && <LabelNew>{t("NEW")}</LabelNew>}
        {value.title.value ? value.title.value : "--"}
      </FlexHorizontal>
    </Typography>
  );
};

const QuestionnaireScore = ({
  score,
}: {
  score: QuestionnaireTableRow["score"];
}) => {
  const hideSensitive = useDemo();
  if (!score) return "--";

  return (
    <FlexHorizontal>
      {!hideSensitive && score.value !== "--" && (
        <StatusIndicator variant={score.status ?? "primary"} />
      )}
      <Typography variant="body">{score.value ? score.value : "--"}</Typography>
    </FlexHorizontal>
  );
};

const LabelNew = styled.span`
  color: ${({ theme }) => theme.COLORS.BLUE_DARK};
`;

const FlexHorizontal = styled.div<
  CreateTransient<{ gap?: keyof (typeof defaultTheme)["SPACING"] }>
>`
  display: flex;
  align-items: center;
  gap: ${({ $gap }) => $gap && defaultTheme.SPACING[$gap]};
`;
